import pako from "pako";
import { validateCRM, validateDate } from "../../../utils";
import parseCsv from "../../../utils/parseCsv";
import validators from "../../../utils/validators";
import { generateErrorCsv as sharedGenerateErrorCsv, downloadErrorCsvFile } from "../../../utils/errorCsvUtils";
import { SERVER_URL } from "../../../config";

/**
 * Valida se a descrição tem código (apenas dígitos), separador (espaço ou hífen) e texto do produto.
 * Exemplos válidos: "173220 - PREDSIM SOLUCAO 15ML AG", "209050 ALIVIUM GTS 100MG/ML 10ML AG"
 * Exemplos inválidos: "197xx420" (sem separador), "17ti89ii3-0 DIPROSPAN" (letras no código), "17893-0" (só número após o hífen)
 */
export const validateDescription = (description) => {
  if (!description || typeof description !== "string") return false;
  const trimmed = description.trim();
  if (trimmed.length <= 5) return false;
  // Código deve ser só dígitos; deve existir pelo menos um espaço ou hífen antes da descrição
  const match = trimmed.match(/^(\d+)[\s-]+(.*)$/);
  if (!match) return false;
  const afterCode = match[2].trim();
  // Descrição do produto: pelo menos 3 caracteres e ao menos uma letra
  return afterCode.length >= 3 && /[A-Za-z]/.test(afterCode);
};

/**
 * Valida se é um número
 */
export const validateNumeric = (value) => {
  if (!value) return false;
  return /^\d+$/.test(String(value).trim());
};

/**
 * Valida uma data no formato DD/MM/YYYY ou DD/MM/YY
 */
export const validateDateFormat = (date) => {
  if (!date) return false;
  return validateDate(date);
};

/**
 * Valida uma linha do CSV
 */
export const validateRow = (row, index, missingColumns = []) => {
  const errors = [];
  const rowNumber = index + 2; // +2 porque linha 1 é header e index começa em 0

  // Verificar colunas obrigatórias faltando
  if (missingColumns.length > 0) {
    missingColumns.forEach((col) => {
      errors.push(`COLUNA OBRIGATÓRIA ${col.toUpperCase()} NÃO LOCALIZADA NO ARQUIVO`);
    });
  }

  // Validar campos obrigatórios
  const requiredFields = {
    crm: "CRM",
    "nomedomedico": "Nome do Médico",
    descricao: "DESCRIÇÃO",
    lote: "LOTE",
    "qtd.item": "QTD. ITEM",
    "datadebaixa": "Data de Baixa",
    "datafabricacao": "DATA FABRICAÇÃO",
    "datadepostagem": "Data de Postagem",
    "notafiscal": "NOTA FISCAL",
  };

  Object.keys(requiredFields).forEach((key) => {
    const fieldName = requiredFields[key];
    const value = row[key];

    if (!value || (typeof value === "string" && value.trim() === "")) {
      errors.push(`Campo ${fieldName} é obrigatório`);
    }
  });

  // Validar CRM
  if (row.crm) {
    if (!validateCRM(row.crm)) {
      errors.push("CRM INVÁLIDO");
    }
  }

  // Validar DESCRIÇÃO
  if (row.descricao) {
    if (!validateDescription(row.descricao)) {
      errors.push("Campo DESCRIÇÃO deve conter código e descrição do produto");
    }
  }

  // Validar QTD. ITEM - deve ser numérico e maior que zero (0 é inválido)
  const qtdVal = row["qtd.item"];
  const hasQtdValue = qtdVal !== undefined && qtdVal !== null && String(qtdVal).trim() !== "";
  if (hasQtdValue) {
    if (!validateNumeric(qtdVal) || Number(qtdVal) <= 0) {
      errors.push("Campo QTD. ITEM deve ser um valor numérico maior que zero");
    }
  }

  // Validar datas
  const dateFields = {
    "datadebaixa": "DATA DE BAIXA",
    "datafabricacao": "DATA FABRICAÇÃO",
    "datadepostagem": "DATA DE POSTAGEM",
  };

  Object.keys(dateFields).forEach((key) => {
    if (row[key] && !validateDateFormat(row[key])) {
      errors.push(`Campo ${dateFields[key]} deve ser uma data válida no formato DD/MM/YYYY`);
    }
  });

  return {
    rowNumber,
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Valida o CSV completo e retorna resultado com erros
 */
export const validateCsvFile = async (csvText, originalHeaders = []) => {
  try {
    // Parse do CSV
    const rows = await parseCsv(csvText, "consolidated-delivered-orders").catch((e) => {
      throw new Error("Erro ao processar CSV: " + e.message);
    });

    if (!rows || !rows.length) {
      throw new Error("CSV vazio ou inválido");
    }

    // Validar headers
    const columns = Object.keys(rows[0]);
    const validHeaders = validators.validateCsvHeaders(columns, "consolidated-delivered-orders");

    const missingColumns = [];
    if (validHeaders && validHeaders.errors) {
      validHeaders.errors.forEach((error) => {
        missingColumns.push(error.column);
      });
    }

    // Validar cada linha
    const validationResults = rows.map((row, index) => validateRow(row, index, missingColumns));

    const hasErrors = validationResults.some((result) => !result.isValid);
    const totalErrors = validationResults.reduce(
      (sum, result) => sum + result.errors.length,
      0
    );

    return {
      isValid: !hasErrors && missingColumns.length === 0,
      rows,
      validationResults,
      missingColumns,
      totalErrors,
      originalHeaders,
    };
  } catch (error) {
    return {
      isValid: false,
      error: error.message,
      rows: [],
      validationResults: [],
      missingColumns: [],
      totalErrors: 0,
      originalHeaders,
    };
  }
};

/**
 * Gera CSV com erros
 */
export const generateErrorCsv = (originalCsvText, validationResults, originalHeaders = [], options = {}) =>
  sharedGenerateErrorCsv(originalCsvText, validationResults, originalHeaders, {
    statusColumnName: "Status de processamento",
    errorColumnName: "Motivo do erro",
    ...options,
  });

/**
 * Faz download do CSV de erros
 */
export const downloadErrorCsv = (errorCsv, filename = "erros-validacao") => {
  return downloadErrorCsvFile(errorCsv, filename);
};

/**
 * Faz upload do CSV de erros no S3 (body comprimido em gzip para evitar payload grande).
 * Retorna a resposta da API (provavelmente com URL ou ID do arquivo).
 * Backend deve aceitar Content-Encoding: gzip e descomprimir nesta rota.
 */
export const uploadErrorCsvToS3 = async (errorCsv, filename = "erros-validacao.csv") => {
  try {
    const token = localStorage.getItem("token");
    const payload = {
      csvContent: errorCsv,
      filename: filename.endsWith(".csv") ? filename : `${filename}.csv`,
    };
    const jsonString = JSON.stringify(payload);
    const compressed = pako.gzip(new TextEncoder().encode(jsonString));

    const COMPRESSED_SIZE_LIMIT_MB = 20;
    if (compressed.length > COMPRESSED_SIZE_LIMIT_MB * 1024 * 1024) {
      throw new Error(`O arquivo excedeu o limite de tamanho. Envie um arquivo menor.`);
    }

    const response = await fetch(`${SERVER_URL}/consolidated-delivered-orders/invalid-csv`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Content-Encoding": "gzip",
        Authorization: `Bearer ${token}`,
      },
      body: compressed,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Erro ao fazer upload" }));
      const isSizeError = response.status === 413 || /tamanho|limite|exceeded|too large|413/i.test(error.message || "");
      throw new Error(isSizeError ? "O arquivo excedeu o limite de tamanho. Envie um arquivo menor." : error.message || "Erro ao fazer upload do CSV");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Erro ao fazer upload do CSV de erros:", error);
    throw error;
  }
};

/**
 * Faz download do CSV de erros via rota de download
 * (Ajuste a rota conforme sua implementação no backend)
 */
export const downloadErrorCsvFromServer = async (fileIdOrUrl) => {
  try {
    const token = localStorage.getItem("token");
    
    // Ajuste esta rota conforme sua implementação
    // Exemplo: GET /consolidated-delivered-orders/invalid-csv/:id
    // ou se for URL direta, pode usar diretamente
    const response = await fetch(`${SERVER_URL}/consolidated-delivered-orders/invalid-csv/${fileIdOrUrl}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Erro ao fazer download do CSV");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `erros-validacao-csv.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Erro ao fazer download do CSV:", error);
    throw error;
  }
};

/**
 * Faz upload do CSV de erros e depois faz o download
 */
export const downloadErrorCsvToS3 = async (errorCsv, filename = "erros-validacao") => {
  try {
    // 1. Fazer upload no S3
    const uploadResponse = await uploadErrorCsvToS3(errorCsv, filename);
    
    // 2. Se a resposta contém URL direta do S3, abrir diretamente
    if (uploadResponse.s3Url || uploadResponse.url) {
      window.open(uploadResponse.s3Url || uploadResponse.url, "_blank");
      return;
    }
    
    // 3. Se a resposta contém ID, usar rota de download
    if (uploadResponse.id || uploadResponse.fileId) {
      await downloadErrorCsvFromServer(uploadResponse.id || uploadResponse.fileId);
      return;
    }
    
    // 4. Fallback: tentar usar a resposta completa como URL
    if (typeof uploadResponse === "string") {
      window.open(uploadResponse, "_blank");
      return;
    }
    
    throw new Error("Resposta do servidor não contém URL ou ID válido");
  } catch (error) {
    console.error("Erro ao processar download do CSV:", error);
    throw error;
  }
};

/**
 * Faz download do CSV de erros via rota de download usando o nome da descrição
 */
export const downloadErrorCsvByDescription = async (description) => {
  try {
    const token = localStorage.getItem("token");
    
    // Sanitizar o nome da descrição para usar como parâmetro
    const sanitizeForUrl = (filename) => {
      if (!filename) return "";
      return filename
        .replace(/[^a-zA-Z0-9\s\-_]/g, "")
        .replace(/\s+/g, "-")
        .trim();
    };
    
    const sanitizedDescription = sanitizeForUrl(description);
    
    if (!sanitizedDescription) {
      throw new Error("Nome da descrição é obrigatório para fazer download");
    }
    
    // Chamar a rota passando o nome da descrição como query parameter
    const response = await fetch(
      `${SERVER_URL}/consolidated-delivered-orders/error-csv?description=${encodeURIComponent(sanitizedDescription)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Erro ao fazer download do CSV" }));
      throw new Error(error.message || "Erro ao fazer download do CSV");
    }

    // Fazer download do blob retornado
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sanitizedDescription}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Erro ao fazer download do CSV:", error);
    throw error;
  }
};

/**
 * Faz download do template CSV padrão
 */
export const downloadTemplateExample = async () => {
  try {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${SERVER_URL}/consolidated-delivered-orders/example-csv`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Erro ao fazer download do template" }));
      throw new Error(error.message || "Erro ao fazer download do template CSV");
    }

    // Fazer download do blob retornado
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "template-integração-crm-farma.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Erro ao fazer download do template CSV:", error);
    throw error;
  }
};